import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { corporateCMSApi } from "~/Services";

interface ContentData {
  markup: string;
  nextPage: {
    name: string;
    link: string;
  };
}

interface BrandingGuidelines {
  id: number;
  field_content_english: {
    data: ContentData;
  };
  field_content_malay: {
    data: ContentData;
  };
  field_content_mandarin: {
    data: ContentData;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface PageHeading {
  id: number;
  field_page_banner_image: string;
  field_page_title_en: string;
  field_page_title_ms?: string;
  field_page_title_zh?: string;
  field_page_description_en?: string;
  field_page_description_ms?: string;
  field_page_description_zh?: string;
  field_page_metadata_image?: string;
  field_page_metadata_title_en: string;
  field_page_metadata_title_ms?: string;
  field_page_metadata_title_zh?: string;
  field_page_metadata_desc_en: string;
  field_page_metadata_desc_ms?: string;
  field_page_metadata_desc_zh?: string;
  field_page_name: string;
  field_page_display_path: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}
interface NewsNextPage {
  id: number;
  title: string;
  field_content_english: {
    data: {
      nextPage: {
        name: string;
        link: string;
      };
    };
  };
  field_content_malay: {
    data: {
      nextPage: {
        name: string;
        link: string;
      };
    };
  };
  field_content_mandarin: {
    data: {
      nextPage: {
        name: string;
        link: string;
      };
    };
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

const useBrandGuidelines = (
  options?: UseQueryOptions<BrandingGuidelines[], DefaultError, BrandingGuidelines[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<BrandingGuidelines[], DefaultError> => {
  return useQuery<BrandingGuidelines[], DefaultError, BrandingGuidelines[], QueryKey>(
    {
      queryKey: ["brandingGuidelinesPageContent"],
      enabled: false,
      queryFn: async (): Promise<BrandingGuidelines[]> => {
        const response = await corporateCMSApi.get<BrandingGuidelines[]>(
          `/api/page-content/all-fields?pageName=media-centre/branding-guidelines`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useBrandGuidelinesPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["brandingGuidelinesPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=media-centre/branding-guidelines`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useArchivesNewsPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["archivesNewsPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=media-centre/news/archives`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useLatestNewsPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["latestNewsPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=media-centre/news/latest`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useLatestNewsNextPage = (
  options?: UseQueryOptions<NewsNextPage[], DefaultError, NewsNextPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<NewsNextPage[], DefaultError> => {
  return useQuery<NewsNextPage[], DefaultError, NewsNextPage[], QueryKey>(
    {
      queryKey: ["latestNewsNextPage"],
      enabled: false,
      queryFn: async (): Promise<NewsNextPage[]> => {
        const response = await corporateCMSApi.get<NewsNextPage[]>(
          `/api/page-content/all-fields?pageName=media-centre/news/latest`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useArchivesNewsNextPage = (
  options?: UseQueryOptions<NewsNextPage[], DefaultError, NewsNextPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<NewsNextPage[], DefaultError> => {
  return useQuery<NewsNextPage[], DefaultError, NewsNextPage[], QueryKey>(
    {
      queryKey: ["archivesNewsNextPage"],
      enabled: false,
      queryFn: async (): Promise<NewsNextPage[]> => {
        const response = await corporateCMSApi.get<NewsNextPage[]>(
          `/api/page-content/all-fields?pageName=media-centre/news/archives`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useNewsPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["newsPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=media-centre/news`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

export {
  useArchivesNewsNextPage,
  useArchivesNewsPageHeading,
  useBrandGuidelines,
  useBrandGuidelinesPageHeading,
  useLatestNewsNextPage,
  useLatestNewsPageHeading,
  useNewsPageHeading,
};
